// ParticleBackground — DISABLED 2026-06-26 per Commander directive.
// The 80-particle animated mesh with connection lines at 0.5 opacity
// rendered as a noisy blue/gray static across the app — read as
// "ugly and cheap" against the Swiss Brutalist + gold-on-charcoal
// brand language. Removing the visual entirely beats trying to tune
// it; pure dark backdrop carries the brand better.
//
// Original implementation preserved below for revert if needed; the
// export is a no-op stub.
import React from 'react';

const ParticleBackground = () => null;

export default ParticleBackground;

// ─── disabled original (kept for reverse-revert) ──────────────────
const _ParticleBackgroundOriginal = () => {
  const canvasRef = useRef(null);
  const { theme } = useTheme();
  const particlesRef = useRef([]);
  const animationRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Create particles with varied properties
    const particleCount = 80;
    particlesRef.current = [];

    for (let i = 0; i < particleCount; i++) {
      const isAccent = i % 3 === 0;
      particlesRef.current.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius: Math.random() * 1.5 + 0.8,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        opacity: Math.random() * 0.4 + 0.2,
        isAccent: isAccent,
        pulse: Math.random() * Math.PI * 2,
        pulseSpeed: 0.02 + Math.random() * 0.02
      });
    }

    // Mouse interaction
    const handleMouseMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('mousemove', handleMouseMove);

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Parse colors
      let primaryR = 102, primaryG = 126, primaryB = 234;
      let accentR = 118, accentG = 75, accentB = 162;
      
      if (theme.primary && theme.primary.startsWith('#')) {
        const hex = theme.primary.substring(1);
        primaryR = parseInt(hex.substring(0, 2), 16);
        primaryG = parseInt(hex.substring(2, 4), 16);
        primaryB = parseInt(hex.substring(4, 6), 16);
      }
      
      if (theme.accent && theme.accent.startsWith('#')) {
        const hex = theme.accent.substring(1);
        accentR = parseInt(hex.substring(0, 2), 16);
        accentG = parseInt(hex.substring(2, 4), 16);
        accentB = parseInt(hex.substring(4, 6), 16);
      }

      // Draw particles
      particlesRef.current.forEach((particle, i) => {
        // Update position
        particle.x += particle.vx;
        particle.y += particle.vy;

        // Mouse interaction - particles move away from cursor
        const dx = mouseRef.current.x - particle.x;
        const dy = mouseRef.current.y - particle.y;
        const distanceToMouse = Math.sqrt(dx * dx + dy * dy);
        
        if (distanceToMouse < 120) {
          const force = (120 - distanceToMouse) / 120;
          particle.x -= (dx / distanceToMouse) * force * 2;
          particle.y -= (dy / distanceToMouse) * force * 2;
        }

        // Wrap around edges
        if (particle.x < 0) particle.x = canvas.width;
        if (particle.x > canvas.width) particle.x = 0;
        if (particle.y < 0) particle.y = canvas.height;
        if (particle.y > canvas.height) particle.y = 0;

        // Update pulse
        particle.pulse += particle.pulseSpeed;
        const pulseOpacity = particle.opacity + Math.sin(particle.pulse) * 0.1;

        // Draw glow
        const r = particle.isAccent ? accentR : primaryR;
        const g = particle.isAccent ? accentG : primaryG;
        const b = particle.isAccent ? accentB : primaryB;
        
        const gradient = ctx.createRadialGradient(
          particle.x, particle.y, 0,
          particle.x, particle.y, particle.radius * 3
        );
        gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${pulseOpacity})`);
        gradient.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, ${pulseOpacity * 0.3})`);
        gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
        
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius * 3, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        // Draw connections
        particlesRef.current.forEach((particle2, j) => {
          if (i >= j) return;

          const dx2 = particle.x - particle2.x;
          const dy2 = particle.y - particle2.y;
          const distance = Math.sqrt(dx2 * dx2 + dy2 * dy2);

          if (distance < 120) {
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(particle2.x, particle2.y);
            const lineOpacity = (1 - distance / 120) * 0.15;
            ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${lineOpacity})`;
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        });
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [theme.primary, theme.accent]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed top-0 left-0 w-full h-full pointer-events-none z-0"
      style={{ opacity: 0.5 }}
    />
  );
};
// (the no-op `export default` is at the top of the file)
